import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ViewChildren, QueryList, ElementRef, Input, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, FormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { InventoryService } from '../../services/inventory.service';
import { OrdersService } from '../../services/orders.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { forkJoin, Subject } from 'rxjs';
import { debounceTime, tap, takeUntil } from 'rxjs/operators';
import { ITEM_INVENTORY_STATUS_CHANGE_MIN_LEVEL, userLevelAtLeast } from '../../constants/access-levels';

@Component({
	selector: 'app-inventory-search',
	templateUrl: './inventory-search.component.html',
	styleUrls: ['./inventory-search.component.scss']
})
export class InventorySearchComponent implements OnInit, AfterViewInit, OnDestroy {

	@Input() stockid;
	@ViewChild(MatSort, { static: false }) sort: MatSort;
	@ViewChildren(MatPaginator) paginators: QueryList<MatPaginator>;
	get paginator(): MatPaginator | null { return this.paginators?.first ?? null; }
	@ViewChild('itemSearch') itemSearchRef: ElementRef;
	@ViewChild('tableWrapper') tableWrapper: ElementRef;
	itemSearchControl = new UntypedFormControl('');
	categoryControl = new UntypedFormControl('');
	productLineControl = new UntypedFormControl('');
	searching = false;
	searchfield = [];
	items: any = [];;
	allitems: any = [];
	header: any = [];
	results: any = [];
	inventorysearching: any;
	itemsearchsubscription: any;
	categories: any = false;
	productlines: any = false;
	user: any = false;
	include_disabled = new UntypedFormControl(false);
	headercolumns: string[] = [];

	color: any = 'blue'
	typing = false;
	page_size: any = [25, 50, 100, 500]
	page_size_default: any = 50;
	config: any = false;
	searchBySize: any = false;
	highlightedRowIndex = -1;
	hoveredRowIndex = -1;
	isMobile = window.innerWidth < 768;

	@HostListener('window:resize')
	onResize(): void {
		this.isMobile = window.innerWidth < 768;
	}

	private destroy$ = new Subject<void>();

	constructor(
		private route: ActivatedRoute,
		private formBuilder: UntypedFormBuilder,
		public router: Router,
		private location: Location,
		private inventoryService: InventoryService,
		private orderService: OrdersService,
		private globalSearchService: GlobalSearchService
	) {
		this.loadDataConcurrently();
		this.subscribeToUserUpdates();
		this.globalSearchService.getColor();
		this.globalSearchService.configsubscription.subscribe(r => {
			this.config = r;
			this.columns();
		});
	}

	private loadDataConcurrently(): void {
		forkJoin({
			categories: this.inventoryService.getItemCategories(),
			productlines: this.inventoryService.getItemProductlines()
		}).subscribe(({ categories, productlines }) => {
			this.categories = categories;
			this.productlines = productlines;
		});
	}

	private subscribeToUserUpdates(): void {
		this.globalSearchService.user.subscribe(user => this.user = user);
	}

	getTotalQoh(itemqoh: any[]): number {
		return itemqoh?.reduce((total_qoh, q) =>
			q.loccode === this.user?.user?.defaultlocation?.loccode ?
				total_qoh + parseFloat(q.quantity) :
				total_qoh, 0) || 0;
	}

	toNumber(val: any): number {
		if (val == null || val === '') {
			return 0;
		}
		if (typeof val === 'number' && !Number.isNaN(val)) {
			return val;
		}
		const s = String(val).replace(/[$,]/g, '').trim();
		const n = parseFloat(s);
		return Number.isNaN(n) ? 0 : n;
	}

	getQohPillClass(qoh: number): string {
		if (qoh <= 0) {
			return 'qoh-pill--red';
		}
		if (qoh < 4) {
			return 'qoh-pill--yellow';
		}
		return 'qoh-pill--green';
	}

	ngOnInit(): void {
		this.columns();
		this.itemSearchControl.valueChanges.pipe(
			tap(() => this.typing = true),
			debounceTime(900)
		).subscribe(() => this.searchItems());
	}



	searchItems(): void {
		this.searching = true;
		this.unsubscribeFromPreviousSearch();

		const search = {
			stockid: this.itemSearchControl.value,
			category: this.categoryControl.value,
			productlines: this.productLineControl.value,
			discontinued: this.include_disabled.value,
			size: this.searchBySize
		};

		this.inventorysearching = this.inventoryService.lookup(search).subscribe((items: any[]) => {
			this.typing = false;
			this.searching = false;

			if (items.length === 1) {
				this.clearSearchResults();
				this.viewItem(items[0]);
			} else {
				this.handleMultipleItems(items);
			}
		});
	}

	private unsubscribeFromPreviousSearch(): void {
		if (this.inventorysearching) {
			this.inventorysearching.unsubscribe();
		}
	}

	private clearSearchResults(): void {
		this.allitems = [];
		this.items = [];
		this.itemSearchControl.setValue('');
		this.highlightedRowIndex = -1;
	}

	private handleMultipleItems(items: any[]): void {
		this.page_size = [25, 50, 100, 500, items.length];
		this.page_size_default = Math.min(items.length, 100);

		this.allitems = items;
		this.items = new MatTableDataSource(items);
		this.items.sortingDataAccessor = (data: any, sortHeaderId: string) => {
			if (sortHeaderId === 'qoh') {
				return this.getTotalQoh(data.qoh);
			}
			if (sortHeaderId === 'cost' || sortHeaderId === 'price') {
				return this.toNumber(data[sortHeaderId]);
			}
			const v = data[sortHeaderId];
			return v == null ? '' : v;
		};
		this.items.sort = this.sort;
		setTimeout(() => {
			this.items.paginator = this.paginator;
		});
		this.highlightedRowIndex = -1;
	}

	getCurrentPageRows(): any[] {
		if (!this.items || !(this.items instanceof MatTableDataSource)) {
			return [];
		}
		const data = this.items.filteredData || this.items.data || [];
		if (!this.paginator) {
			return data;
		}
		const start = this.paginator.pageIndex * this.paginator.pageSize;
		return data.slice(start, start + this.paginator.pageSize);
	}

	onSearchKeydown(event: KeyboardEvent): void {
		const rows = this.getCurrentPageRows();
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			if (!rows.length) {
				return;
			}
			event.preventDefault();
			if (event.key === 'ArrowDown') {
				this.highlightedRowIndex = Math.min(this.highlightedRowIndex + 1, rows.length - 1);
			} else {
				this.highlightedRowIndex = Math.max(this.highlightedRowIndex - 1, 0);
			}
			this.scrollHighlightedIntoView();
			return;
		}
		if (event.key === 'Enter') {
			event.preventDefault();
			if (rows.length && this.highlightedRowIndex >= 0 && rows[this.highlightedRowIndex]) {
				this.viewItem(rows[this.highlightedRowIndex]);
			} else if (!rows.length) {
				this.searchItems();
			}
		}
	}

	scrollHighlightedIntoView(): void {
		setTimeout(() => {
			const el = this.tableWrapper?.nativeElement?.querySelectorAll?.('tbody tr')?.[this.highlightedRowIndex];
			if (el) {
				el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
			}
		}, 0);
	}

	clearForm(): void {
		this.itemSearchControl.setValue('');
		this.allitems = [];
		this.items = [];
		this.searching = false;
		this.highlightedRowIndex = -1;
	}

	back(): void {
		this.location.back();
	}

	viewItem(row: any): void {
		this.allitems = [];
		this.items = [];
		this.itemSearchControl.setValue('');
		this.highlightedRowIndex = -1;
		this.router.navigate(['/inventory/view/' + row.stockid]);
	}

	copiedStockId: string | null = null;

	copyToClipboard(stockid: string, event: Event): void {
		event.stopPropagation();
		navigator.clipboard.writeText(stockid).then(() => {
			this.copiedStockId = stockid;
			setTimeout(() => this.copiedStockId = null, 1000);
		});
	}

	isItemEnabled(item: any): boolean {
		const v = item?.enabled;
		if (v === undefined || v === null) {
			return true;
		}
		return v === 1 || v === '1' || v === true;
	}

	canChangeInventoryItemStatus(): boolean {
		return userLevelAtLeast(this.user, ITEM_INVENTORY_STATUS_CHANGE_MIN_LEVEL);
	}

	toggleEnabled(item: any, event: Event): void {
		event.stopPropagation();
		if (!this.canChangeInventoryItemStatus()) {
			return;
		}
		const currentlyEnabled = this.isItemEnabled(item);
		const payload = currentlyEnabled
			? { dis: [item.stockid], enable: [] }
			: { dis: [], enable: [item.stockid] };
		this.inventoryService.batchDisableProducts(payload).subscribe(() => {
			item.enabled = currentlyEnabled ? 0 : 1;
			this.items.data = this.allitems.slice();
			this.globalSearchService.showNotification(
				currentlyEnabled ? 'Item disabled' : 'Item enabled',
				'success', 'bottom', 'left'
			);
		});
	}

	isItemDiscontinued(item: any): boolean {
		const v = item?.discontinued;
		if (v === undefined || v === null) {
			return false;
		}
		return v === 1 || v === '1' || v === true;
	}

	toggleDiscontinued(item: any, event: Event): void {
		event.stopPropagation();
		if (!this.canChangeInventoryItemStatus()) {
			return;
		}
		const currentlyDiscontinued = this.isItemDiscontinued(item);
		const payload = currentlyDiscontinued
			? { dis: [], enable: [item.stockid] }
			: { dis: [item.stockid], enable: [] };
		this.inventoryService.batchDiscontinueProducts(payload).subscribe(() => {
			item.discontinued = currentlyDiscontinued ? 0 : 1;
			this.items.data = this.allitems.slice();
			this.globalSearchService.showNotification(
				currentlyDiscontinued ? 'Item reactivated' : 'Item discontinued',
				'success', 'bottom', 'left'
			);
		});
	}

	ngAfterViewInit(): void {
		if (this.paginator?.page) {
			this.paginator.page.pipe(takeUntil(this.destroy$)).subscribe(() => {
				this.highlightedRowIndex = -1;
			});
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.unsubscribeFromPreviousSearch();
	}

	announceSortChange(input: any): void {
	}

	isBeauty(): boolean {
		return this.config?.env?.package === 'beauty';
	}

	/** When false, on-hand counts are hidden (e.g. AA liveinventory). */
	showQohColumn(): boolean {
		const c = this.config;
		return !c || typeof c !== 'object' || Array.isArray(c) || c.liveinventory !== false;
	}

	columns(): void {
		const beauty = this.isBeauty();
		const showQoh = this.showQohColumn();
		let cols: string[] = [
			'stockid',
			...(beauty ? ['enabled'] : []),
			'supplierno', 'lineid', 'description',
			...(showQoh ? ['qoh'] : []),
			'cost', 'price',
			...(beauty ? ['discontinued'] : [])
		];
		if (this.searchBySize) {
			const i = cols.indexOf('supplierno');
			cols = [...cols.slice(0, i), 'size', ...cols.slice(i)];
		}
		this.headercolumns = cols;
	}

}